import { AlertFactory } from 'alerts/Alert'
import BillingStore from 'billing/BillingStore'
import { NewPaymentMethodDelegate } from 'billing/payment-methods/PaymentMethodsRootViewModel'
import DocumentAccessStore from 'documents/DocumentAccessStore'
import DocumentStore from 'documents/DocumentStore'
import DocumentTagStore from 'documents/DocumentTagStore'
import DocumentVersionStore from 'documents/DocumentVersionStore'
import DocumentRootViewModel from 'documents/views/DocumentRoot/DocumentRootViewModel'
import { ViewDocumentDetailsDelegate } from 'documents/views/DocumentRoot/DocumentDetailsRootViewModel'
import { UserDocumentsViewModelAdapter } from 'documents/views/UserDocumentsViewModelAdapter'
import InvitationStore from 'iam/InvitationStore'
import MemberStore from 'iam/MemberStore'
import Member from 'iam/models/Member'
import RoleStore from 'iam/RoleStore'
import ProfileViewModel from 'iam/screens/Profile/ProfileViewModel'
import { GetCurrentMember } from 'iam/SessionStore'
import SkillStore from 'iam/SkillStore'
import TeamMemberStore from 'iam/TeamMemberStore'
import TeamRoleStore from 'iam/TeamRoleStore'
import { TeamsController } from 'iam/TeamsController'
import TeamStore from 'iam/TeamStore'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import WorkspaceStore from 'iam/WorkspaceStore'
import JobStore from 'jobs/JobStore'
import { action, observable, makeObservable } from 'mobx'
import NotificationPreferencesStore from 'notifications/NotificationPreferencesStore'
import { Observable } from 'rxjs'
import { Id, Router } from 'types/definitions'
import { DocumentActionsAdapter } from 'documents/DocumentActionsAdapter'
import PayoutMethodStore from 'payouts/pro/PayoutMethodStore'

/**
 * View Controlller.
 */
export default class ViewController {
  /**
   * Returns the current member object
   */
  private getCurrentMember: GetCurrentMember = () => this.member

  /**
   * The workspace represented by this view controller.
   */
  workspace = this.member.workspace

  /**
   * The last path (route) that was used before switching away.
   */
  @observable lastPath = ''

  teamsController = new TeamsController(
    this.workspace,
    this.teamMemberStore,
    this.teamStore,
    this.getCurrentMember,
    this.teamRoleStore,
    this.invitationStore,
    this.router,
    this.billingStore,
    this.newPaymentMethod,
    this.newAlert,
  )

  userDocumentsAdapter = new UserDocumentsViewModelAdapter(
    this.getCurrentMember,
    this.getCurrentUserWorkspaceMemberships,
    this.documentStore,
    this.documentVersionStore,
    this.documentTagStore,
    this.documentTagStore.fetchTags.bind(this.documentTagStore),
    this.newAlert,
    this.documentActionsAdapter,
  )

  documentRoot = new DocumentRootViewModel(
    this.userDocumentsAdapter,
    this.viewDocumentDetails,
    this.currentMember$,
  )

  profileViewModel = new ProfileViewModel(
    this.member,
    this.newAlert,
    // IAM Port
    {
      fetchMemberDetails: (member) =>
        this.memberStore.fetch({
          workspace_id: Id.assert(member.workspace.id),
          user_id: Id.assert(member.user.id),
          user_public_id: member.user.publicId,
        }),
      changeAvatar: this.memberStore.changeAvatar.bind(this.memberStore),
      updateProfile: this.memberStore.updateProfile.bind(this.memberStore),
      fetchSkills: (workspace, query) =>
        this.skillStore.find({
          ...query,
          workspace_id: Id.assert(workspace.id),
        }),
      addSkill: this.memberStore.addSkill.bind(this.memberStore),
      removeSkill: this.memberStore.removeSkill.bind(this.memberStore),
      fetchRoles: (workspace) =>
        this.roleStore.find({
          workspace_id: Id.assert(workspace.id),
        }),
      addRole: this.memberStore.addRole.bind(this.memberStore),
      removeRole: this.memberStore.removeRole.bind(this.memberStore),
    },
    // Notifications Port
    {
      getPreferences: this.notificationPreferencesStore.getPreferences.bind(
        this.notificationPreferencesStore,
      ),
      savePreferences: this.notificationPreferencesStore.savePreferences.bind(
        this.notificationPreferencesStore,
      ),
    },
    this.billingStore,
    this.newPaymentMethod,
    this.payoutMethodStore,
    this.router,
  )

  constructor(
    private newAlert: AlertFactory,
    private notificationPreferencesStore: NotificationPreferencesStore,
    private memberStore: MemberStore,
    private jobStore: JobStore,
    private teamStore: TeamStore,
    private teamMemberStore: TeamMemberStore,
    private teamRoleStore: TeamRoleStore,
    private documentStore: DocumentStore,
    private documentVersionStore: DocumentVersionStore,
    private documentAccessStore: DocumentAccessStore,
    private documentTagStore: DocumentTagStore,
    private invitationStore: InvitationStore,
    private roleStore: RoleStore,
    private skillStore: SkillStore,
    private billingStore: BillingStore,
    private workspaceStore: WorkspaceStore,
    public member: Member,
    private router: Router,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private currentMember$: Observable<Member>,
    private viewDocumentDetails: ViewDocumentDetailsDelegate,
    private newPaymentMethod: NewPaymentMethodDelegate,
    private documentActionsAdapter: DocumentActionsAdapter,
    private payoutMethodStore: PayoutMethodStore,
  ) {
    makeObservable(this)
  }

  /**
   * Sets the last path.
   *
   * @param path
   */
  @action.bound
  setLastPath(path: string) {
    this.lastPath = path
  }
}
